import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { ColDef, GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from 'ag-grid-community';

import { BsModalRef } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';

import { FileUtil } from 'rev-shared/util/FileUtil';
import { IIconCellRendererParams } from 'rev-shared/ui/dataGrid/cellRenderers/IconCellRenderer.Component';
import { IVbUiDataGridColDef } from 'rev-shared/ui/dataGrid/columns/IVbUiDataGridColDef';
import { VbUiDataGridComponent } from 'rev-shared/ui/dataGrid/VbUiDataGrid.Component';
import { formatMediumDateTime, formatTimespanShort } from 'rev-shared/date/DateFormatters';
import { handleTabKeyPress } from 'rev-shared/ui/dataGrid/DataGridUtil';

import { ImportExternalMediaService } from './ImportExternalMedia.Service';

import styles from './Import.module.less';

@Component({
	selector: 'base-import-dialog-component',
	template: '',
	standalone: false
})
export abstract class BaseImportDialogComponent implements OnInit {
	@ViewChild(VbUiDataGridComponent) public grid: VbUiDataGridComponent;

	public readonly styles = styles;
	public readonly ImportExternalMediaService = inject(ImportExternalMediaService);
	public readonly ModalInstance = inject(BsModalRef);
	public readonly TranslateService = inject(TranslateService);

	public gridApi: GridApi;
	public gridColumnDefs: IVbUiDataGridColDef[];
	public gridDefaultColDef: ColDef = {};
	public gridOptions: GridOptions = {};
	public isLoading: boolean = false;

	public readonly importStatusCellRendererParams: IIconCellRendererParams = {
		getIconClass: (isImported: boolean): string => isImported ? 'glyphicons circle_ok' : ''
	};

	protected abstract fetchRecordings(): Promise<any[]>;
	protected abstract processRecordings(recordings: any[]): void;
	protected abstract handleError(error: any): void;
	protected abstract clearErrors(): void;
	protected abstract assignDataGridSettings(): void;
	protected abstract assignGridColumnDefs(): void;

	public ngOnInit(): void {
		this.assignDataGridSettings();
		this.loadRecordings();
	}

	public onGridReady(event: GridReadyEvent): void {
		this.gridApi = event.api;
	}

	public closeDialog(): void {
		this.ModalInstance.hide();
	}

	public getSelectedImports(): any[] {
		return this.gridApi?.getSelectedRows();
	}

	public loadRecordings(): void {
		this.isLoading = true;
		this.clearErrors();
		this.fetchRecordings()
			.then(result => {
				this.processRecordings(result);
			})
			.catch(error => {
				this.handleError(error);
			})
			.finally(() => {
				this.isLoading = false;
			});
	}

	public assignGridDefaultColDef(): void {
		this.gridDefaultColDef = {
			suppressMovable: true,
			suppressKeyboardEvent: handleTabKeyPress
		};
	}

	public assignGridOptions(): void {
		this.gridOptions = {
			isRowSelectable: rowNode => !rowNode.data.isImported,
			onRowSelected: (event: RowSelectedEvent) => {
				const rowNode = event.node;
				rowNode.data.isSelected = rowNode.isSelected();
			}
		};
	}

	public getCommonColumnDefs(nameField: string, createdField: string, sizeField: string, excludedFields: string[] = []): IVbUiDataGridColDef[] {
		const allColumnDefs: IVbUiDataGridColDef[] = [
			{
				checkboxSelection: true,
				headerCheckboxSelection: true,
				headerCheckboxSelectionFilteredOnly: true,
				showDisabledCheckboxes: true,
				maxWidth: 50
			},
			{
				field: nameField,
				headerName: this.TranslateService.instant('Name'),
				sortable: true,
				tooltipField: nameField
			},
			{
				field: 'isImported',
				headerName: this.TranslateService.instant('Imported'),
				sortable: true,
				tooltipValueGetter: data => data.value ? this.TranslateService.instant('Imported') : '',
				cellRenderer: 'icon',
				cellRendererParams: this.importStatusCellRendererParams,
				minWidth: 150,
				maxWidth: 250
			},
			{
				field: createdField,
				headerName: this.TranslateService.instant('Created'),
				sortable: true,
				tooltipValueGetter: data => formatMediumDateTime(data.value),
				valueFormatter: data => formatMediumDateTime(data.value),
				minWidth: 200,
				maxWidth: 250
			},
			{
				field: 'durationInMs',
				headerName: this.TranslateService.instant('Duration'),
				sortable: true,
				tooltipValueGetter: data => formatTimespanShort(data.value),
				valueFormatter: data => formatTimespanShort(data.value),
				maxWidth: 100
			},
			{
				field: sizeField,
				headerName: this.TranslateService.instant('Size'),
				sortable: true,
				tooltipField: sizeField,
				tooltipValueGetter: data => FileUtil.formatFileSize(data.value),
				valueFormatter: data => FileUtil.formatFileSize(data.value),
				maxWidth: 150
			}
		];

		return allColumnDefs.filter(colDef => !excludedFields.includes(colDef.field));
	}

}
